import {
  type LessonItem,
  addDays,
  clubsOnDay,
  useAbroadHome,
  useClubMemberships,
  useClubs,
  useEvents,
  useLessons,
} from '@shkola/api';
import { subjectLook } from '@shkola/design';
import { useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PageHead, SUBJECT_GRAD } from '../design/forms';
import { type PlanItem, PlannerBlock, minutesOf, usePlanner } from '../schedule/Planner';
import { eventsPlan, lessonPlan, useFirstLessonDay } from '../schedule/lessons';
import { placeOf, zoneShift } from './AbroadPage';
import { forStudent } from './home/GlassDay';
import { type StudentContext, isoWeekday } from './home/model';
import { ShapesLessonCard } from './home/ShapesDay';

/*
 * «Розклад» of the student — and of the family, who see the child's — as a planner (28.10, his refs of
 * planners and calendars: «как людям привычнее»): the week on a time axis with every lesson a block of
 * its subject exactly in its time, the clubs after the lessons, the school's events over the days and
 * the holidays tinted; the month and the day a tap away. A lesson opens its card (the topic, what to
 * bring, the homework due, the way in for the student; the lesson's pack).
 */

export function ShapesSchedule({ ctx, family = false }: { ctx: StudentContext; family?: boolean }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const studentId = ctx.child.memberId;
  const scope = useMemo(() => ({ kind: 'student' as const, studentId }), [studentId]);
  const first = useFirstLessonDay(ctx.schoolId, scope);
  const planner = usePlanner(first.date);
  const lessons = useLessons(ctx.schoolId, scope, planner.range.from, planner.range.to);
  const events = useEvents(ctx.schoolId, planner.range.from);
  const clubs = useClubs(ctx.schoolId);
  const memberships = useClubMemberships([studentId]);
  const home = useAbroadHome(studentId).data;
  const [open, setOpen] = useState<{ lesson: LessonItem; el: HTMLElement }>();

  const happenings = eventsPlan(
    t,
    (events.data ?? []).filter(forStudent(ctx)),
    planner.range,
    () => void navigate({ to: '/events' }),
  );
  const notes = happenings.notes;
  const holiday = (d: string) => notes.some((n) => n.holiday && n.startsOn <= d && d <= n.endsOn);
  const all = lessons.data ?? [];
  const items: PlanItem[] = [
    ...all.map((l) => lessonPlan(t, l, 'student', (el) => setOpen({ lesson: l, el }))),
    ...happenings.items,
  ];
  // the clubs on the days that have lessons (not on the holidays)
  for (let d = planner.range.from; d <= planner.range.to; d = addDays(d, 1)) {
    if (holiday(d) || !all.some((l) => l.date === d && l.status !== 'cancelled')) continue;
    for (const { club, slot } of clubsOnDay(
      clubs.data ?? [],
      memberships.data ?? [],
      studentId,
      isoWeekday(d),
    )) {
      items.push({
        id: `club-${club.id}-${d}-${slot.startsAt}`,
        date: d,
        start: minutesOf(slot.startsAt),
        end: minutesOf(slot.endsAt),
        title: club.title,
        ...(club.room && { sub: club.room }),
        tone: 'lime',
        marks: [],
        club: true,
        onOpen: () => void navigate({ to: '/clubs' }),
      });
    }
  }

  const child = family
    ? [ctx.child.firstName, ctx.child.class?.label].filter(Boolean).join(' · ')
    : '';
  return (
    <div className="flex flex-col gap-4">
      <PageHead
        title={t('schedule.title')}
        {...(child && { note: child })}
        sub={home ? `${placeOf(home)} · ${zoneShift(t, home.timezone)}` : undefined}
      />
      <PlannerBlock
        planner={planner}
        items={first.ready ? items : []}
        notes={notes}
        pending={!first.ready || lessons.isPending}
        groupTone={(g) => SUBJECT_GRAD[subjectLook(g).tone]}
      />
      {open && (
        <ShapesLessonCard
          ctx={ctx}
          lesson={open.lesson}
          anchor={open.el}
          onClose={() => setOpen(undefined)}
          family={family}
          inSchedule
        />
      )}
    </div>
  );
}
